import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Camera, Video, Users, GraduationCap, Car, Sparkles, Aperture, Lightbulb, MapPin } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { MagneticButton } from '@/components/MagneticButton';
import { GalleryCard } from '@/components/GalleryCard';
import { CTASection } from '@/components/CTASection';
import { ShowreelSection } from '@/components/ShowreelSection';
import { TestimonialsCarousel } from '@/components/TestimonialsCarousel';
import { InstagramStrip } from '@/components/InstagramStrip';
import { SEO } from '@/components/SEO';
import { ParticleField } from '@/components/ParticleField';
import { SpotlightGlow } from '@/components/SpotlightGlow';
import { ClipReveal } from '@/components/ClipReveal';
import { SectionDivider } from '@/components/SectionDivider';
import { useParallax } from '@/lib/useParallax';
import { useSpotlight } from '@/lib/useSpotlight';
import { AI_FEATURES_ENABLED } from '@/lib/featureFlags';
import { Skeleton } from '@/components/ui/skeleton';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { getGalleries } from '@/lib/api';
import { SITE_IMAGES, SITE_VIDEOS, HOME_HERO_VIDEOS } from '@/lib/siteImages';
import { HOME_PAGE, INTELLIGENCE_ENTRY } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';
import { HowItWorksTeaser } from '@/components/howItWorks/HowItWorksTeaser';
import { HeroVideoRotator } from '@/components/HeroVideoRotator';

const HIGHLIGHTS = [
  { icon: Camera, title: 'Photography', desc: 'Portraits, events, cars and more.' },
  { icon: Video, title: 'Videography', desc: 'Cinematic videos that bring your story to life.' },
  { icon: Users, title: 'Events', desc: 'From intimate moments to big celebrations.' },
  { icon: GraduationCap, title: 'Graduations', desc: 'Celebrate your achievement with timeless photos.' },
  { icon: Car, title: 'Automotive', desc: 'High quality car photography and cinematic reels.' },
];

export default function Home() {
  const config = useSiteConfig();
  const [galleries, setGalleries] = useState(null);
  const [error, setError] = useState(false);
  const parallax = useParallax(90);
  const spotlight = useSpotlight();

  useEffect(() => {
    getGalleries()
      .then(setGalleries)
      .catch(() => setError(true));
  }, []);

  return (
    <div>
      <SEO
        title="Home"
        description="LXRA Studios is a cinematic photography & videography studio serving the DMV. Portraits, events, graduations, automotive shoots and more."
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          name: 'LXRA Studios',
          image: SITE_IMAGES.homeHero,
          description: 'Cinematic photography & videography studio serving the DMV (Washington DC, Maryland & Virginia). Portraits, events, graduations, automotive & more.',
          telephone: config.phone,
          email: config.email,
          areaServed: config.service_area,
          url: typeof window !== 'undefined' ? window.location.origin : undefined,
          sameAs: [config.instagram_url].filter(Boolean),
          priceRange: '$$',
        }}
      />
      {/* HERO */}
      <section
        ref={(node) => { parallax.ref.current = node; spotlight.ref.current = node; }}
        onMouseMove={spotlight.onMouseMove}
        onMouseEnter={spotlight.onMouseEnter}
        onMouseLeave={spotlight.onMouseLeave}
        className="relative min-h-[92vh] flex items-center lxra-grain overflow-hidden"
        data-testid={HOME_PAGE.hero}
      >
        <div className="absolute inset-0">
          <motion.div style={{ y: parallax.y }} className="absolute inset-x-0 -top-[15%] h-[130%]">
            <HeroVideoRotator videos={HOME_HERO_VIDEOS} />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          <ParticleField count={16} />
          <SpotlightGlow pos={spotlight.pos} active={spotlight.active} />
        </div>


        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10 pt-24">
          <Reveal>
            <p className="text-xs sm:text-sm tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-4">
              Cinematic Visuals.
            </p>
            <h1 className="font-display leading-[0.92] tracking-[0.02em] text-[hsl(var(--lxra-fg))] text-6xl sm:text-7xl lg:text-8xl">
              <TextReveal text="REAL" />
              <br />
              <TextReveal text="MOMENTS" delay={0.08} /><span className="text-[hsl(var(--lxra-gold))]">.</span>
            </h1>
            <div className="mt-5 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
            <p className="mt-6 max-w-md text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
              Photography and videography that capture emotion, tell your story, and create lasting impact.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <MagneticButton>
                <Link to="/galleries" data-testid={HOME_PAGE.heroViewGalleries}>
                  <button className="inline-flex items-center h-12 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-semibold">
                    View Galleries
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </Link>
              </MagneticButton>
              <MagneticButton>
                <Link to="/book" data-testid={HOME_PAGE.heroBookNow}>
                  <button className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-sm uppercase tracking-[0.1em] font-semibold bg-transparent">
                    Book Now
                  </button>
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE HIGHLIGHTS STRIP */}
      <section className="bg-[hsl(var(--lxra-cream))] text-[#0A0A0A]" data-testid={HOME_PAGE.highlightsStrip}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-6">
            {HIGHLIGHTS.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="group flex flex-col items-center text-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-black/15 text-[#0A0A0A] transition-transform duration-200 group-hover:-translate-y-1">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold tracking-[0.06em] uppercase">{item.title}</p>
                    <p className="mt-1 text-xs text-black/60 leading-relaxed max-w-[140px] mx-auto">{item.desc}</p>
                  </div>
                  <span className="h-[2px] w-0 bg-[hsl(var(--lxra-gold))] transition-all duration-300 group-hover:w-8" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED GALLERIES */}
      <section className="bg-[#0A0A0A]" data-testid={HOME_PAGE.featuredGalleries}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-2">Explore My Work</p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-[0.04em] text-[hsl(var(--lxra-fg))]">GALLERIES</h2>
              </div>
              <Link
                to="/galleries"
                data-testid={HOME_PAGE.viewAllGalleries}
                className="text-xs sm:text-sm uppercase tracking-[0.12em] text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors flex items-center gap-2"
              >
                View All Galleries
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          {error && (
            <p className="text-sm text-[hsl(var(--lxra-muted))]">Unable to load galleries right now. Please try again shortly.</p>
          )}

          {!galleries && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              <SkeletonList count={5} className="aspect-[3/4] rounded-2xl bg-[#141416]" />
            </div>
          )}

          {galleries && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {galleries.map((cat, i) => (
                <Reveal key={cat.slug} delay={i * 0.05}>
                  <GalleryCard category={cat} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-[hsl(var(--lxra-cream))] text-[#0A0A0A]" data-testid={HOME_PAGE.aboutPreview}>
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <ClipReveal className="relative min-h-[360px] lg:min-h-[520px]">
            <video
              className="absolute inset-0 h-full w-full object-cover"
              src={SITE_VIDEOS.homeAboutPreview}
              poster={SITE_IMAGES.homeAboutPreview}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              data-testid="home-about-preview-video"
            />
          </ClipReveal>
          <Reveal delay={0.1} className="flex items-center">
            <div className="px-6 sm:px-10 lg:px-16 py-16">
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold-hover))] mb-3">About LXRA Studios</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.02em] leading-[1.02]">
                MORE THAN PHOTOS.
                <br />
                IT&apos;S YOUR LEGACY.
              </h2>
              <div className="mt-4 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
              <p className="mt-5 max-w-md text-sm sm:text-base text-black/70 leading-relaxed">
                We&apos;re dedicated to capturing the moments that matter most. With a cinematic eye and
                professional approach, we create visuals across the DMV that you&apos;ll cherish forever.
              </p>
              <MagneticButton>
                <Link
                  to="/about"
                  data-testid={HOME_PAGE.aboutLearnMore}
                  className="mt-7 inline-flex items-center h-11 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* LXRA INTELLIGENCE */}
      <section className="bg-[#0A0A0A] border-y border-[hsl(var(--lxra-border))]" data-testid={INTELLIGENCE_ENTRY.homeSection}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">
              <Sparkles className="h-4 w-4" /> LXRA Intelligence
              {!AI_FEATURES_ENABLED && (
                <span className="ml-1 rounded-full border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 px-2 py-0.5 text-[10px] tracking-[0.1em] text-[hsl(var(--lxra-gold))]">
                  Coming Soon
                </span>
              )}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.02em] leading-[1.02] text-[hsl(var(--lxra-fg))]">
              PHOTOGRAPHY CAPTURES REALITY.
              <br />
              TECHNOLOGY EXPANDS POSSIBILITY.
              <br />
              <span className="text-[hsl(var(--lxra-gold))]">STORY GIVES IT MEANING.</span>
            </h2>
            <div className="mt-4 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
            <p className="mt-5 max-w-md text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
              Before the first frame, LXRA Intelligence turns your story into a Creative Blueprint — direction, shot list,
              lighting, and locations across the DMV. The camera and the photographer stay at the center. Technology
              simply helps us plan it beautifully.
            </p>
            <MagneticButton>
              <Link
                to="/plan"
                data-testid={INTELLIGENCE_ENTRY.homeCta}
                className="mt-7 inline-flex items-center h-11 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold"
              >
                Plan Your Session
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </MagneticButton>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Aperture, title: 'Creative Direction', text: 'A visual story, palette, and shot list sequenced scene by scene.' },
              { icon: Lightbulb, title: 'Light & Timing', text: 'The best window for your location, with a plan if the weather turns.' },
              { icon: MapPin, title: 'DMV Locations', text: 'Real places across DC, Maryland & Virginia matched to your aesthetic.' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={0.1 + i * 0.06}>
                <div className="h-full rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/30 text-[hsl(var(--lxra-gold))]">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-xl tracking-[0.04em] text-[hsl(var(--lxra-fg))] mt-5">{f.title}</h3>
                  <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <HowItWorksTeaser />

      <ShowreelSection />

      <TestimonialsCarousel />

      <InstagramStrip />

      <CTASection
        kicker="Ready to capture your story?"
        title="BOOK YOUR SESSION TODAY."
        testId={HOME_PAGE.ctaBanner}
        buttonTestId={HOME_PAGE.ctaBookNow}
      />
    </div>
  );
}
